import { FC, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useFormContext, useWatch } from 'react-hook-form';
import { skipToken } from '@reduxjs/toolkit/query';
import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';

import { useGetFolderAssetsQuery } from '../../../services';
import { usePlacematContext } from '../../Workspaces/Pages/PlacematGeneratorPage/PlacematContext';
import { FolderManagerGridItem } from '../../FolderManager/Components/FolderManagerGridItem';
import { StyledGridView } from '../../FolderManager/Components/folderManagerGrid.shared';

const parseFolderId = (sourcePath: string): number | null => {
  const match = sourcePath?.match(/crn:folders:(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const EMPTY_ASSETS: any[] = [];

const StyledEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  line-height: 1.5;
`;

const StyledSlotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 12px 0;
`;

const StyledActiveSlotLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const StyledClearSlotButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.danger ?? '#dc2626'};
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }
`;

interface SlidesPickerInputProps {
  data: SlidesPickerDynamicFormField;
  prefix?: string;
  noTargetMessage?: string | null;
}

export const SlidesPickerInput: FC<SlidesPickerInputProps> = ({ data, prefix = '', noTargetMessage }) => {
  const { control, setValue } = useFormContext();
  const { activeSlot } = usePlacematContext();
  const inputName = prefix + data.name;

  const folderId = parseFolderId(data.inputOptions?.sourcePath);
  const { data: rawAssets, isLoading } = useGetFolderAssetsQuery(
    folderId ? { parentId: folderId } : skipToken,
  );
  const assets = rawAssets ?? EMPTY_ASSETS;

  const slides = useMemo(
    () =>
      assets.flatMap((asset, assetIndex) =>
        (asset.slides || []).map((slide: any, slideIndex: number) => ({
          ...slide,
          id: `${(asset as any)?.currentVersion?.ID}-${slideIndex}`,
          name: slide.displayName || `${asset.name} - Slide ${slideIndex + 1}`,
          rowIndex: assetIndex,
          status: 'completed',
        })),
      ),
    [assets],
  );

  const slideIds = useMemo(() => slides.map((s) => String(s.id)), [slides]);

  const fieldValue: Record<string, string> = useWatch({ control, name: inputName }) ?? {};

  // Ref holds latest values so the stable onSelect callback never goes stale
  // (FolderManagerGridItemContent memo skips callback comparisons intentionally)
  const latestRef = useRef({ activeSlot, fieldValue, inputName });
  latestRef.current = { activeSlot, fieldValue, inputName };

  const handleSelect = useCallback(
    ({ item }: { event: any; item: any }) => {
      const { activeSlot, fieldValue, inputName } = latestRef.current;
      if (!activeSlot) return;
      const next = { ...fieldValue, [activeSlot]: item.id };
      setValue(inputName, next, { shouldDirty: true });
    },
    [setValue],
  );

  const selectedSlideId = activeSlot ? fieldValue[activeSlot] : null;
  const selectedRows = activeSlot
    ? slides.filter((slide) => String(slide.id) === String(selectedSlideId))
    : [];

  if (isLoading) {
    return <StyledEmptyState>Loading slides…</StyledEmptyState>;
  }

  if (noTargetMessage) {
    return <StyledEmptyState>{noTargetMessage}</StyledEmptyState>;
  }

  if (slides.length === 0) {
    return <StyledEmptyState>No slides found in this folder</StyledEmptyState>;
  }

  return (
    <>
      <StyledSlotHeader>
        <StyledActiveSlotLabel>
          {activeSlot ? `Assigning to: ${activeSlot}` : 'Select a slot in the layout, then click a slide'}
        </StyledActiveSlotLabel>
        {activeSlot && fieldValue[activeSlot] && (
          <StyledClearSlotButton
            type="button"
            onClick={() => {
              const { activeSlot, fieldValue, inputName } = latestRef.current;
              const { [activeSlot!]: _removed, ...next } = fieldValue;
              setValue(inputName, next, { shouldDirty: true });
            }}
          >
            Clear slot
          </StyledClearSlotButton>
        )}
      </StyledSlotHeader>
      <DndContext>
        <SortableContext items={slideIds}>
          <StyledGridView $columnsCount={2} $compactGrid>
            {slides.map((slide) => (
              <FolderManagerGridItem
                key={slide.id}
                item={slide}
                selectedRows={selectedRows}
                disableDnD
                compact
                hideActions
                hidePreviewIcon
                onSelect={handleSelect}
              />
            ))}
          </StyledGridView>
        </SortableContext>
      </DndContext>
    </>
  );
};
